import React, { useState } from 'react';
import { FileCode2, Check, X, Loader2, RotateCcw, ShieldCheck, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Inline receipt card rendered inside Aria's chat bubble for each code
 * change she made. Edits are AUTO-APPLIED server-side the moment Aria's
 * SSE stream lands — this card is a read-only record showing the diff,
 * the file path, and the live status (applied / failed / rebuilding).
 * No approval button: the result is in the preview iframe itself.
 *
 * The legacy `pending` state (with Approve / Reject buttons) is kept as
 * a defensive fallback for older edits still sitting unapproved in the DB.
 */
export default function EditProposalCard({ edit: initialEdit, onApproved }) {
  const [edit, setEdit] = useState(initialEdit);
  const [busy, setBusy] = useState(null); // 'approve' | 'reject' | null
  const [err, setErr] = useState(null);

  const status = edit?.status || 'pending';
  const isPending = status === 'pending';
  const isApplied = status === 'applied' || status === 'approved';
  const isFailed = status === 'failed';
  const isRejected = status === 'rejected';
  const filePath = edit?.file_path || '';
  const fileName = filePath.split('/').pop() || filePath;
  const folder = filePath.replace(/\/?[^/]+$/, '') || '/';

  const accent = isApplied ? '#22C55E'
    : isFailed ? '#B91C1C'
    : isRejected ? 'rgba(15,15,30,0.45)'
    : '#7C3AED';

  const handle = async (action) => {
    setBusy(action);
    setErr(null);
    try {
      const r = await fetch(`${API}/api/edits/${edit.edit_id}/${action}`, {
        method: 'POST', credentials: 'include',
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setErr(data.detail || `Failed to ${action}`);
        return;
      }
      const newStatus = action === 'approve' ? (data.status || 'applied') : 'rejected';
      setEdit(e => ({ ...e, status: newStatus }));
      if (action === 'approve') onApproved?.(edit, data);
    } catch (e) {
      setErr(e?.message || 'Network error');
    } finally {
      setBusy(null);
    }
  };

  const badgeLabel = isApplied ? 'Applied'
    : isFailed ? 'Needs retry'
    : isRejected ? 'Rejected'
    : 'Aria proposes';

  return (
    <div
      data-testid={`edit-proposal-${edit.edit_id}`}
      style={{
        margin: '12px 0 6px',
        background: '#FFFFFF',
        border: `1px solid ${accent}55`,
        borderLeft: `3px solid ${accent}`,
        borderRadius: 10,
        boxShadow: '0 1px 2px rgba(15,15,30,0.04), 0 8px 22px rgba(15,15,30,0.06)',
        overflow: 'hidden',
        animation: 'fadeIn 0.22s ease-out',
      }}
    >
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '11px 14px 9px',
        background: `linear-gradient(135deg, ${accent}0E, transparent 70%)`,
        borderBottom: '1px solid rgba(15,15,30,0.05)',
      }}>
        <FileCode2 size={14} style={{ color: accent, flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 11.5, fontWeight: 700, color: '#0F0F1A',
            display: 'flex', alignItems: 'baseline', gap: 6,
            fontFamily: "'JetBrains Mono', monospace",
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {folder && folder !== '/' && (
              <span style={{ color: 'rgba(15,15,30,0.45)' }}>{folder}/</span>
            )}
            <span>{fileName}</span>
          </div>
          {edit.description && (
            <div style={{
              fontSize: 11, color: 'rgba(15,15,30,0.65)',
              marginTop: 2, lineHeight: 1.45,
            }}>{edit.description}</div>
          )}
        </div>
        <span style={{
          fontSize: 8.5, fontWeight: 700, letterSpacing: '0.12em',
          padding: '3px 7px',
          color: accent,
          background: `${accent}14`,
          border: `1px solid ${accent}33`,
          textTransform: 'uppercase',
          flexShrink: 0,
        }}>{badgeLabel}</span>
      </div>

      {/* Diff body */}
      <DiffPreview oldText={edit.old_text} newText={edit.new_text} />

      {/* Footer */}
      <div style={{
        padding: '10px 14px',
        background: 'rgba(15,15,30,0.015)',
        borderTop: '1px solid rgba(15,15,30,0.05)',
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, flex: 1 }}>
          <ShieldCheck size={11} style={{ color: 'rgba(15,15,30,0.45)' }} />
          <span style={{ fontSize: 10, color: 'rgba(15,15,30,0.55)', lineHeight: 1.4 }}>
            Edited your <strong style={{ color: 'rgba(15,15,30,0.7)' }}>local cloned repo</strong> — not GitHub.
          </span>
        </div>
        {isPending && (
          <>
            <button
              onClick={() => handle('reject')}
              disabled={!!busy}
              data-testid={`edit-reject-${edit.edit_id}`}
              style={btnSecondary(busy === 'reject')}
            >
              {busy === 'reject' ? <Loader2 size={11} className="spin-anim" /> : <X size={12} />}
              Reject
            </button>
            <button
              onClick={() => handle('approve')}
              disabled={!!busy}
              data-testid={`edit-approve-${edit.edit_id}`}
              style={btnPrimary(busy === 'approve')}
            >
              {busy === 'approve' ? <Loader2 size={11} className="spin-anim" /> : <Check size={12} />}
              Apply
            </button>
          </>
        )}
        {isApplied && (
          <span style={{ fontSize: 11, fontWeight: 700, color: '#22C55E', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <Check size={12} /> Applied · preview rebuilding
          </span>
        )}
        {isFailed && (
          <span style={{ fontSize: 11, fontWeight: 700, color: '#B91C1C', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <AlertTriangle size={12} /> {edit.error ? String(edit.error).slice(0, 80) : 'Could not apply — ask Aria to retry'}
          </span>
        )}
        {isRejected && (
          <span style={{ fontSize: 11, fontWeight: 600, color: 'rgba(15,15,30,0.55)', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <RotateCcw size={11} /> Rejected — no change applied
          </span>
        )}
      </div>

      {isFailed && edit.hint && (
        <div style={{
          padding: '8px 14px 10px',
          fontSize: 10.5, color: 'rgba(15,15,30,0.62)',
          background: 'rgba(185,28,28,0.04)',
          borderTop: '1px solid rgba(185,28,28,0.12)',
          fontFamily: "'JetBrains Mono', monospace",
          whiteSpace: 'pre-wrap', lineHeight: 1.5,
        }}>
          <div style={{ fontWeight: 700, color: '#B91C1C', marginBottom: 4, fontSize: 9.5, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            Closest match in file
          </div>
          {edit.hint}
        </div>
      )}

      {err && (
        <div style={{
          padding: '8px 14px 10px',
          fontSize: 11, color: '#B91C1C',
          background: 'rgba(239,68,68,0.06)',
          borderTop: '1px solid rgba(239,68,68,0.15)',
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <AlertTriangle size={11} /> {err}
        </div>
      )}
    </div>
  );
}

function DiffPreview({ oldText, newText }) {
  const isCreate = !oldText && newText;
  const isDelete = oldText && !newText;
  return (
    <div style={{
      fontFamily: "'JetBrains Mono', monospace",
      fontSize: 11.5, lineHeight: 1.55,
      maxHeight: 280, overflowY: 'auto',
      background: '#FBFBF7',
    }}>
      {!isCreate && (
        <DiffBlock label="−" text={oldText} tone="remove" />
      )}
      {!isDelete && (
        <DiffBlock label="+" text={newText} tone="add" />
      )}
    </div>
  );
}

function DiffBlock({ label, text, tone }) {
  const colors = tone === 'add'
    ? { bg: 'rgba(34,197,94,0.06)', border: 'rgba(34,197,94,0.3)', fg: '#0F5132' }
    : { bg: 'rgba(239,68,68,0.05)', border: 'rgba(239,68,68,0.25)', fg: '#6F1D1B' };
  const lines = (text || '').split('\n');
  return (
    <div style={{
      padding: '6px 12px 6px 6px',
      background: colors.bg,
      borderTop: `1px solid ${colors.border}`,
      color: colors.fg,
    }}>
      {lines.map((line, i) => (
        <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
          <span style={{
            width: 14, flexShrink: 0, textAlign: 'center',
            color: colors.border, fontWeight: 700,
          }}>{label}</span>
          <pre style={{
            margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            flex: 1, fontFamily: 'inherit',
          }}>{line || ' '}</pre>
        </div>
      ))}
    </div>
  );
}

const btnSecondary = (busy) => ({
  padding: '6px 11px',
  fontSize: 11, fontWeight: 700, fontFamily: 'inherit',
  background: 'transparent',
  color: 'rgba(15,15,30,0.65)',
  border: '1px solid rgba(15,15,30,0.14)',
  borderRadius: 6,
  cursor: busy ? 'not-allowed' : 'pointer',
  display: 'inline-flex', alignItems: 'center', gap: 4,
  letterSpacing: '0.02em',
});

const btnPrimary = (busy) => ({
  padding: '6px 13px',
  fontSize: 11, fontWeight: 800, fontFamily: 'inherit',
  background: 'linear-gradient(135deg, #7C3AED, #00A88C)',
  color: '#0F0F1A',
  border: 'none',
  borderRadius: 6,
  cursor: busy ? 'not-allowed' : 'pointer',
  display: 'inline-flex', alignItems: 'center', gap: 5,
  letterSpacing: '0.03em', textTransform: 'uppercase',
  boxShadow: '0 4px 12px rgba(124,58,237,0.32)',
});
